'use client';

import Image from 'next/image';
import Link from 'next/link';
import { Star, Play, Plus, Info, Check } from 'lucide-react';
import { tmdb } from '@/lib/tmdb';

export interface CinematicHeroProps {
  backdropPath?: string | null;
  title: string;
  overview?: string;
  rating?: number;
  year?: number | null;
  badgeLabel?: string;
  href: string;
  /** Optional add-to-list handler; shows a "+"/check button when provided. */
  onAdd?: () => void;
  inList?: boolean;
}

/**
 * Premium Cinematic hero: large immersive backdrop, layered gradients, a strong
 * bottom fade into black, a soft red glow accent, and high-contrast actions.
 */
export function CinematicHero({
  backdropPath,
  title,
  overview,
  rating,
  year,
  badgeLabel = 'Trending Now',
  href,
  onAdd,
  inList,
}: CinematicHeroProps) {
  return (
    <section className="relative h-[88vh] min-h-[560px] w-full overflow-hidden">
      {backdropPath && (
        <Image
          src={tmdb.getImageUrl(backdropPath, 'original')}
          alt={title}
          fill
          sizes="100vw"
          className="object-cover"
          style={{ objectPosition: 'center 25%' }}
          priority
        />
      )}
      {/* Cinematic layered gradients */}
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-black/10" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/95 via-black/30 to-transparent" />
      {/* Strong bottom fade so the hero blends smoothly into the content below */}
      <div className="absolute bottom-0 inset-x-0 h-64 bg-gradient-to-t from-black via-black/90 to-transparent" />
      {/* Soft red glow accent */}
      <div className="absolute -bottom-40 -left-40 h-[500px] w-[500px] rounded-full bg-red-600/20 blur-[120px] pointer-events-none" />

      <div className="absolute inset-0 flex items-end">
        <div className="px-5 md:px-10 pb-20 md:pb-28 max-w-3xl">
          <span className="inline-flex items-center gap-2 rounded-full bg-red-600/90 px-3 py-1 text-xs font-semibold uppercase tracking-wider mb-5 text-white">
            {badgeLabel}
          </span>
          <h1 className="text-4xl md:text-7xl font-bold tracking-tight leading-[1.05] mb-5 drop-shadow-2xl text-white">
            {title}
          </h1>
          <div className="flex items-center gap-4 text-sm md:text-base text-gray-300 mb-5">
            {rating ? (
              <span className="flex items-center gap-1.5">
                <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                {rating.toFixed(1)}
              </span>
            ) : null}
            {year && <span>{year}</span>}
            <span className="rounded border border-white/25 px-2 py-0.5 text-xs">HD</span>
          </div>
          {overview && (
            <p className="text-gray-300 text-base md:text-lg leading-relaxed line-clamp-3 mb-8 max-w-2xl">
              {overview}
            </p>
          )}
          <div className="flex items-center gap-3">
            <Link
              href={href}
              className="flex items-center gap-2 rounded-full bg-white text-black font-semibold px-7 py-3.5 hover:bg-gray-200 transition-colors"
            >
              <Play className="h-5 w-5 fill-black" /> Watch
            </Link>
            <Link
              href={href}
              className="flex items-center gap-2 rounded-full bg-white/10 backdrop-blur-md text-white font-semibold px-7 py-3.5 ring-1 ring-white/20 hover:bg-white/20 transition-colors"
            >
              <Info className="h-5 w-5" /> More Info
            </Link>
            {onAdd && (
              <button
                aria-label={inList ? 'In your list' : 'Add to list'}
                onClick={onAdd}
                className="flex items-center justify-center rounded-full bg-white/10 backdrop-blur-md h-12 w-12 ring-1 ring-white/20 hover:bg-white/20 transition-colors text-white"
              >
                {inList ? <Check className="h-5 w-5 text-red-400" /> : <Plus className="h-5 w-5" />}
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
